'use client';

import { tracking } from '@/lib/features/tracking';
import type { UserProfile } from '@/types';
import { Icon } from '@iconify/react';
import { useState } from 'react';
import { ChatChartComponent } from '../components/chat-chart-component';
import { DocumentAttachment } from '../components/document-attachment';
import { PlanSummaryCard } from '../components/planner-mode/plan-summary-card';
import { ReportLink } from '../components/report-link';
import { ReportProgressBar } from '../components/report-progress-bar';
import { TableDisplay } from '../components/table-display';
import { TextDisplay } from '../components/text-display';
import { ROLE_TYPES, STREAMING_EVENT_TYPES, UI_VARIANTS } from '../constants';
import { ClipBlockView } from '../reels/components/ClipBlockView';
import { VideoProgressBlockView } from '../reels/components/VideoProgressBlockView';
import { VideoThemesBlockView } from '../reels/components/VideoThemesBlockView';
import type { ChatMessage, ChatMessageContent } from '../types';
import { getExtractionMethodFromExtension, parseEmbeddedDocument } from './document-extraction';
import { parseReportSegments } from './report-block';
import { convertResultSetToMarkdown } from './streaming-utils';

function ThinkingBlock({ text }: { text: string }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="rounded-lg border border-default-200/60 bg-default-50 dark:bg-default-100/30">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left"
            >
                <Icon
                    icon="lucide:brain"
                    width={13}
                    height={13}
                    className="shrink-0 text-default-500"
                />
                <span className="flex-1 text-tiny font-medium text-default-600">Thinking</span>
                <Icon
                    icon={open ? 'lucide:chevron-up' : 'lucide:chevron-down'}
                    width={12}
                    height={12}
                    className="text-default-400"
                />
            </button>
            {open && (
                <div className="border-t border-default-200/60 px-3 py-2">
                    <p className="whitespace-pre-wrap text-tiny text-default-500">{text}</p>
                </div>
            )}
        </div>
    );
}

interface RenderTextContentResult {
    textElements: React.ReactElement[];
    documentElements: React.ReactElement[];
}

/**
 * Renders text content, handling embedded documents for user messages
 * Returns separate arrays for text and document elements
 */
function renderTextContent(
    text: string,
    role: ChatMessage['role'],
    baseKey: string,
    variant?: typeof UI_VARIANTS.TEXT_DISPLAY.BUBBLE | typeof UI_VARIANTS.TEXT_DISPLAY.PLAIN,
): RenderTextContentResult {
    const textElements: React.ReactElement[] = [];
    const documentElements: React.ReactElement[] = [];

    // For user messages, check if the text contains an embedded document
    if (role === ROLE_TYPES.USER) {
        const parsed = parseEmbeddedDocument(text);

        // Render the user message text
        if (parsed.userMessage) {
            textElements.push(
                <TextDisplay
                    key={`${baseKey}-text`}
                    text={parsed.userMessage}
                    role={role}
                    variant={variant}
                />,
            );
        }

        // If there's an embedded document, add it
        if (parsed.document) {
            const ext = parsed.document.fileName.split('.').pop()?.toLowerCase();
            const extractionMethod = getExtractionMethodFromExtension(ext);

            documentElements.push(
                <DocumentAttachment
                    key={`${baseKey}-doc`}
                    fileName={parsed.document.fileName}
                    extractedText={parsed.document.extractedText}
                    extractionMethod={extractionMethod}
                    className="mt-2"
                />,
            );
        }
    } else {
        // The planner agent appends a `{"type":"report","report":{...}}`
        // JSON block to its final assistant text on report-completion turns
        // (see `_finalize_output_text` in mfour-agents). Split the text on
        // those blocks so each becomes a `ReportLink` card while the
        // surrounding prose continues to render through `TextDisplay`.
        const segments = parseReportSegments(text);
        if (segments.length === 0) {
            textElements.push(
                <TextDisplay key={`${baseKey}-text`} text={text} role={role} variant={variant} />,
            );
        } else {
            segments.forEach((seg, idx) => {
                if (seg.kind === 'text') {
                    textElements.push(
                        <TextDisplay
                            key={`${baseKey}-text-${idx}`}
                            text={seg.text}
                            role={role}
                            variant={variant}
                        />,
                    );
                } else {
                    textElements.push(
                        <ReportLink
                            key={`${baseKey}-report-${idx}`}
                            reportId={seg.report.id}
                            title={seg.report.title}
                        />,
                    );
                }
            });
        }
    }

    return { textElements, documentElements };
}

/**
 * Renders message content into organized React elements
 * Separates thinking content from regular content and orders them appropriately
 * Only includes thinking content for internal users
 */
export function renderMessageContent(
    message: ChatMessage,
    user?: UserProfile,
    options?: {
        onExportFullData?: () => void;
        onSendMessage?: (text: string, contextData?: Record<string, unknown>) => void;
    },
): {
    thinkingContent: React.ReactElement[];
    regularContent: React.ReactElement[];
} {
    // Handle legacy string content
    if (typeof message.content === 'string') {
        const { textElements, documentElements } = renderTextContent(
            message.content,
            message.role,
            `legacy-${message.id}`,
        );

        return {
            thinkingContent: [],
            regularContent: [...textElements, ...documentElements],
        };
    }

    // Array content - separate thinking from regular content
    const contentArray = message.content as ChatMessageContent[];
    const thinkingContent: React.ReactElement[] = [];
    const regularContent: React.ReactElement[] = [];

    // Collect document attachments separately to render after text
    const documentAttachments: React.ReactElement[] = [];

    const hasStructuredCard = contentArray.some(
        (c) => c.type === STREAMING_EVENT_TYPES.PLAN_SUMMARY,
    );

    contentArray.forEach((content, index) => {
        const baseKey = `${message.id}-${content.type}-${index}`;

        switch (content.type) {
            case STREAMING_EVENT_TYPES.THINKING: {
                const thinkingText = content.text ?? content.thinking?.text ?? '';
                if (thinkingText.trim()) {
                    thinkingContent.push(<ThinkingBlock key={baseKey} text={thinkingText} />);
                }
                break;
            }

            case STREAMING_EVENT_TYPES.TEXT:
                if (hasStructuredCard) break;
                if (content.text && content.text.trim()) {
                    const { textElements, documentElements } = renderTextContent(
                        content.text.trim(),
                        message.role,
                        baseKey,
                        UI_VARIANTS.TEXT_DISPLAY.BUBBLE,
                    );
                    regularContent.push(...textElements);
                    documentAttachments.push(...documentElements);
                }
                break;

            case STREAMING_EVENT_TYPES.CHART:
                if (content.chart?.chart_spec) {
                    try {
                        const chartSpec = JSON.parse(content.chart.chart_spec);
                        chartSpec.width = 450;
                        if (
                            chartSpec &&
                            typeof chartSpec === 'object' &&
                            chartSpec.data &&
                            chartSpec.mark
                        ) {
                            regularContent.push(
                                <ChatChartComponent key={baseKey} chartSpec={chartSpec} />,
                            );
                        }
                    } catch (error) {
                        tracking.errorWithSource(error as Error, 'content-renderer.tsx', {
                            chartSpec: content.chart.chart_spec,
                        });
                    }
                } else {
                    tracking.errorWithSource(
                        new Error('Chart content missing chart_spec'),
                        'content-renderer.tsx',
                        {
                            content,
                        },
                    );
                }
                break;

            case STREAMING_EVENT_TYPES.TABLE:
                if (content.table) {
                    // Use markdown if available, otherwise convert result_set to markdown
                    let tableMarkdown = content.table.markdown;

                    if (!tableMarkdown && content.table.result_set) {
                        // Convert result_set to markdown (handles API-loaded messages)
                        tableMarkdown = convertResultSetToMarkdown(content.table.result_set);
                    }

                    if (tableMarkdown) {
                        regularContent.push(
                            <TableDisplay
                                key={baseKey}
                                tableMarkdown={tableMarkdown}
                                collapsible={content.table.isToolResult || false}
                                title={content.table.title}
                                onExportFullData={options?.onExportFullData}
                            />,
                        );
                    }
                }
                break;

            case STREAMING_EVENT_TYPES.TOOL_RESULT:
                // Not displaying tool_result directly
                break;

            case STREAMING_EVENT_TYPES.FETCHED_TABLE:
                if (content.fetched_table?.tableMarkdown) {
                    regularContent.push(
                        <TableDisplay
                            key={baseKey}
                            tableMarkdown={content.fetched_table.tableMarkdown}
                            collapsible={content.fetched_table.toolResult || false}
                            onExportFullData={options?.onExportFullData}
                        />,
                    );
                }
                break;

            case STREAMING_EVENT_TYPES.TOOL_USE:
                // Not displaying tool_use directly
                break;

            case STREAMING_EVENT_TYPES.SQL_EXECUTING:
                // This is handled by the chat state, not as content
                break;

            case 'document_attachment':
                // Collect document attachments to render after text
                if (content.document_attachment) {
                    documentAttachments.push(
                        <DocumentAttachment
                            key={baseKey}
                            fileName={content.document_attachment.fileName}
                            fileSize={content.document_attachment.size}
                            contentType={content.document_attachment.contentType}
                            extractedText={content.document_attachment.extractedText}
                            extractionMethod={content.document_attachment.extractionMethod}
                            className="mt-2"
                        />,
                    );
                }
                break;

            case STREAMING_EVENT_TYPES.PLAN_SUMMARY:
                if (content.plan_summary) {
                    regularContent.push(
                        <PlanSummaryCard
                            key={baseKey}
                            payload={content.plan_summary}
                            onCreateReport={
                                options?.onSendMessage
                                    ? (runId, editedPrompts) =>
                                          options.onSendMessage!('Run report', {
                                              runId,
                                              action: 'run_report',
                                              ...(editedPrompts && editedPrompts.length > 0
                                                  ? { editedPrompts }
                                                  : {}),
                                          })
                                    : undefined
                            }
                        />,
                    );
                }
                break;

            case STREAMING_EVENT_TYPES.AGENT_OPTIONS:
                break;

            case STREAMING_EVENT_TYPES.REPORT_PROGRESS:
                if (content.report_progress != null) {
                    regularContent.push(
                        <ReportProgressBar
                            key={baseKey}
                            percent={content.report_progress.percent}
                            status={content.report_progress.status}
                        />,
                    );
                }
                break;

            case STREAMING_EVENT_TYPES.VIDEO_PROGRESS:
                if (content.video_progress) {
                    regularContent.push(
                        <VideoProgressBlockView key={baseKey} block={content.video_progress} />,
                    );
                }
                break;

            case STREAMING_EVENT_TYPES.VIDEO_THEMES:
                if (content.video_themes) {
                    regularContent.push(
                        <VideoThemesBlockView key={baseKey} block={content.video_themes} />,
                    );
                }
                break;

            case STREAMING_EVENT_TYPES.CLIP:
                if (content.clip) {
                    regularContent.push(<ClipBlockView key={baseKey} block={content.clip} />);
                }
                break;

            default:
                break;
        }
    });

    // Add document attachments after regular content (so they appear below text)
    regularContent.push(...documentAttachments);

    return { thinkingContent, regularContent };
}
